import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { brandTheme } from '../styles/BrandTheme';
import { SERVICE_AREA_ROUTE_SLUGS } from '../data/serviceAreaBlogMapping';
import { getRegionSlugs, getTopicSlugs } from '../data/seoArticleLibrary';

const SitemapContainer = styled.div`
  min-height: 100vh;
  background: ${brandTheme.colors.neutral.white};
  padding: ${brandTheme.spacing[8]} 0;
  font-family: ${brandTheme.typography.fontFamily.mono};
`;

const SitemapContent = styled.pre`
  background: ${brandTheme.colors.neutral.gray[50]};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: ${brandTheme.spacing[6]};
  margin: 0 auto;
  max-width: 1200px;
  overflow-x: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${brandTheme.colors.secondary[800]};
  white-space: pre-wrap;
  word-wrap: break-word;
  box-shadow: ${brandTheme.boxShadow.lg};
  
  /* Remove default link styling */
  a {
    color: ${brandTheme.colors.secondary[800]} !important;
    text-decoration: none !important;
  }
  
  a:hover {
    color: ${brandTheme.colors.primary[600]} !important;
    text-decoration: underline !important;
  }
  
  /* Style XML tags */
  .xml-tag {
    color: ${brandTheme.colors.primary[600]};
    font-weight: ${brandTheme.typography.fontWeight.semibold};
  }
  
  .xml-attr {
    color: ${brandTheme.colors.secondary[600]};
  }
  
  .xml-value {
    color: ${brandTheme.colors.secondary[800]};
  }
  
  @media (max-width: 768px) {
    padding: ${brandTheme.spacing[4]};
    font-size: 0.75rem;
    margin: 0 ${brandTheme.spacing[4]};
  }
`;

const SitemapPage: React.FC = () => {
  const seoGuideUrls = getTopicSlugs()
    .map(
      (slug) => `  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/guides/${slug}/</loc>
    <lastmod>2025-05-06</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.82</priority>
  </url>`
    )
    .join('\n\n');

  const seoRegionUrls = getRegionSlugs()
    .map(
      (slug) => `  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/regions/${slug}/</loc>
    <lastmod>2025-05-06</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.82</priority>
  </url>`
    )
    .join('\n\n');

  const londonBlogUrls = SERVICE_AREA_ROUTE_SLUGS.map(
    (slug) => `  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/london/${slug}/</loc>
    <lastmod>2025-03-26</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.85</priority>
  </url>`
  ).join('\n\n');

  const sitemapContent = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">
  
  <!-- Main Pages - Highest Priority -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>daily</changefreq>
    <priority>1.0</priority>
    <image:image>
      <image:loc>https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg</image:loc>
      <image:title>Resin Surfaces London - Premium Resin Bound Surfacing</image:title>
      <image:caption>Professional resin bound driveway, pathway and pool deck installation across London</image:caption>
    </image:image>
  </url>

  <!-- Service Pages - High Priority -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/services</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>
  
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-london/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>
  
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-pathways-london/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>
  
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-pool-decks-london/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <!-- Information Pages - High Priority -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/what-is-resin-bound-surfacing/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/about-us/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/gallery/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/areas-we-cover/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/contact/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/faq/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.7</priority>
  </url>

  <!-- Blog Pages - High Priority for Content Marketing -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/ultimate-guide-resin-bound-surfacing/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-driveway-cost-guide/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-pathway-design-ideas/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-surfacing-maintenance-tips/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/pool-deck-surfacing-benefits/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-vs-concrete-comparison/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-bound-installation-process/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/seasonal-care-weather-considerations/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/diy-vs-professional-installation/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/blog/resin-bound-troubleshooting-guide/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>

  <!-- SEO topic guides (data-driven) -->
${seoGuideUrls}

  <!-- SEO region guides -->
${seoRegionUrls}

  <!-- London borough blog guides -->
${londonBlogUrls}

  <!-- London Borough Pages - High Priority for Local SEO -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-westminster/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-camden/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-islington/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-hackney/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-tower-hamlets/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-lewisham/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-southwark/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-lambeth/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-wandsworth/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-hammersmith-fulham/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-kensington-chelsea/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-richmond-upon-thames/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston-upon-thames/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-merton/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-sutton/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-croydon/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-bromley/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-bexley/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-havering/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-redbridge/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-waltham-forest/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-enfield/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-barnet/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-harrow/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-hillingdon/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-ealing/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-hounslow/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-brent/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-haringey/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-newham/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/resin-driveways-barking-dagenham/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>

  <!-- Legal Pages - Lower Priority but Important -->
  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/terms-conditions/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>yearly</changefreq>
    <priority>0.3</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/privacy-policy/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>yearly</changefreq>
    <priority>0.3</priority>
  </url>

  <url>
    <loc>https://www.resinsurfaceslondon.co.uk/cookies/</loc>
    <lastmod>2025-01-15</lastmod>
    <changefreq>yearly</changefreq>
    <priority>0.3</priority>
  </url>

</urlset>`;

  return (
    <>
      <Helmet>
        <title>Sitemap - Resin Surfaces London</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <SitemapContainer>
        <SitemapContent>
          {sitemapContent}
        </SitemapContent>
      </SitemapContainer>
    </>
  );
};

export default SitemapPage;
